<template>
  <el-form
    ref="formRef"
    :model="formData"
    @input="updateHandler(formRef)"
    @change="updateHandler(formRef)"
  >
    <el-form-item
      prop="leftText"
      :rules="[{ required: true, message: translateText('hubstr.editor.errors.required_field'), trigger: 'blur' },
               { validator: uniqueValidator, message: 'Поле с таким именем уже было создано' }]"
    >
      <template #label>
        {{ translateText('hubstr.editor.modal.field_name') }}
        <question-tooltip :text="translateText('hubstr.editor.modal.hint_list_line_raw')" />
      </template>
      <el-input
        v-model="titleProxy"
        :maxlength="40"
      />
    </el-form-item>
    <el-form-item :label="translateText('hubstr.editor.modal.icon')">
      <el-select v-model="formData.leftIcon">
        <el-option
          v-for="option in icons"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        >
          <div class="option">
            <component :is="option.icon" class="list-line__icon" />
            <span>{{ option.label }}</span>
          </div>
        </el-option>
      </el-select>
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.additions.isUserRequired" :label="translateText('hubstr.editor.fields.required_app')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.additions.isAdminRequired" :label="translateText('hubstr.editor.fields.required_admin')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.hasPrivacy" :label="translateText('hubstr.editor.fields.has_privacy')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox
        v-if="formData.additions.variableType === 'text' || formData.additions.variableType === 'array'"
        v-model="formData.hasAutoComplete"
        :label="translateText('hubstr.editor.modal.add_autocomplete')"
        :disabled="formData.additions.isMultiline"
      />
    </el-form-item>
    <form-autocomplete
      v-if="formData.hasAutoComplete"
      :field-code="formData.additions.code"
      @update:autocomplete="autocompleteData = $event"
    />
  </el-form>
</template>

<script setup lang="ts">
import {
  reactive, watch, ref, onMounted, 
} from 'vue';
import translit from 'features/settings/editor/utils/translit';
import { v4 as uuidV4 } from 'uuid';
import People from 'features/settings/editor/assets/icons/people.svg';
import Peoples from 'features/settings/editor/assets/icons/peoples.svg';
import PieChart from 'features/settings/editor/assets/icons/pie_chart.svg';
import Suitcase from 'features/settings/editor/assets/icons/suitcase.svg';
import Folder from 'features/settings/editor/assets/icons/folder.svg';
import FolderFull from 'features/settings/editor/assets/icons/folder_full.svg';
import { WIDGET_TYPES } from 'features/settings/editor/association-editor/ui/components/left-side/components/widgets/types/constants';
import {
  IListLineRawActionWidget,
  IListLineWidget,
} from 'features/settings/editor/association-editor/ui/components/left-side/components/widgets/types/interfaces';
import { TranslatesAssociationList as t } from 'features/settings/editor/association-editor/requests';
import { FormInstance } from 'element-plus';
import { translate, useTranslate } from 'magner';
import QuestionTooltip from 'features/settings/mobile-editor/components/left-side/components/question-tooltip.vue';
import useEditorStore from 'features/settings/editor/store/useEditorStore';
import FormAutocomplete
  from 'features/settings/editor/association-editor/ui/components/left-side/modals/components/form-autocomplete.vue';
import { MOBILE_VALIDATORS } from 'features/settings/mobile-editor/constants';

const { customT } = useTranslate();
const translateText = (code: string) => customT(translate(code));

interface Props {
  data?: IListLineRawActionWidget;
  isEdit?: boolean;
  isNew?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(['update:formData', 'input:error', 'update:autocomplete']);
const store = useEditorStore();

const state = (props.data && JSON.parse(JSON.stringify(props.data))) || undefined;
const copy = (props.data && JSON.parse(JSON.stringify(props.data))) as IListLineWidget || undefined;

const formRef = ref<FormInstance>();
const autocompleteData = ref();

const formData = reactive<IListLineRawActionWidget>(
  state || {
    id: uuidV4(),
    type: WIDGET_TYPES.LIST_LINE_RAW,
    leftText: '',
    name: null,
    rightText: '',
    leftIcon: 'suitcase',
    rightIcon: null,
    isMultiline: true,
    dataProvider: null,
    hasAutoComplete: false,
    hasPrivacy: false,
    additions: {
      code: '',
      variableType: 'text',
      allowSearch: false,
      isUserRequired: false,
      isAdminRequired: false,
      glType: 'association',
      entityType: 'association',
      isAutocompletedField: false,
      format: 'DEFAULT',
      hasDisplayType: false,
      formCode: '',
      description: null,
      isMultiline: false,
      mobileValidator: {},
    },
  } as IListLineRawActionWidget,
);

const titleProxy = ref(t[formData.leftText?.replace(/lang\.mobile(_ml)?\.?/g, '')] || formData.leftText);

const icons = [
  { label: 'People', value: 'people', icon: People },
  { label: 'Peoples', value: 'peoples', icon: Peoples },
  { label: 'Pie chart', value: 'pie_chart', icon: PieChart },
  { label: 'Suitcase', value: 'suitcase', icon: Suitcase },
  { label: 'Folder', value: 'folder', icon: Folder },
  { label: 'Folder full', value: 'folder_full', icon: FolderFull },
];

const fieldBuilder = () => {
  if (!props.isEdit || formData.isNew) {
    // "code" меняется только при создании нового поля(или редактировании нового(!) поля)
    const code = `${formData.additions.entityType}${translit(formData.leftText, 'pascal')}`;
    formData.additions.code = code;
    formData.additions.formCode = `data.additions.${code}`;
    formData.rightText = `data.additions.${code}`;
  }
  // если есть перевод оставляем код, если нет то свое значение
  if (titleProxy.value?.toLowerCase() !== t[formData.additions.code]?.toLowerCase()) {
    formData.leftText = titleProxy.value.trim();
    formData.additions.description = titleProxy.value.trim();
  } else {
    formData.leftText = `lang.${formData.additions.code}`;
    formData.additions.description = null;
  }

  if (formData.additions.variableType === 'link') {
    formData.name = 'link';
  }
  if (formData.additions.variableType !== 'text') {
    formData.hasAutoComplete = false;
    formData.additions.isMultiline = false;
  }

  if (formData.hasAutoComplete) {
    formData.additions.isMultiline = false;
    formData.additions.isAutocompletedField = true;
    formData.additions.allowSearch = true;
    formData.additions.mobileValidator = MOBILE_VALIDATORS.AUTOCOMPLETE;
  } else {
    formData.additions.isAutocompletedField = false;
    formData.additions.allowSearch = false;
    formData.additions.mobileValidator = MOBILE_VALIDATORS.TEXT;
  }

  if (props.isNew) {
    formData.isNew = true; // переменная определяет новое ли поле
  }
  formData.touch = true; // переменная определяет изменилась ли formData
};

const updateHandler = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  fieldBuilder();

  formEl.validate((valid) => {
    if (valid) {
      // submit

      const out = JSON.parse(JSON.stringify(formData));
      if (out.hasAutoComplete) {
        // особенность бекэнда
        out.additions.variableType = 'array';
      }
      
      emit('input:error', false);
      emit('update:formData', out);
    } else {
      emit('input:error', true);
    }
  });
};

const uniqueValidator = (rule: any, value: string, callback: any) => {
  if (copy?.additions.formCode !== formData?.additions.formCode && store.associationAdditionalCodes.value.includes(formData.additions.formCode)) {
    callback(new Error());
  } else {
    callback();
  }
};

watch(
  () => formData,
  () => {
    updateHandler(formRef.value);
  },
  { deep: true },
);

/**
 * Принудительно с большой буквы
 * */
watch(() => titleProxy.value, (newValue) => {
  titleProxy.value = newValue.charAt(0).toUpperCase() + newValue.slice(1);
});

watch(() => autocompleteData.value, () => {
  emit('update:autocomplete', { field: formData.additions.code, ...autocompleteData.value });
  emit('input:error', false);
}, { deep: true });

onMounted(() => {
  // особенность бекэнда
  if (formData.additions.variableType === 'array') {
    formData.additions.variableType = 'text';
  }
});
</script>

<style scoped>
.list-line__icon {
  width: 20px;
}
.option {
  padding: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.el-checkbox {
  font-weight: lighter;
}
</style>
